import { useState, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Badge,
  Box,
  Stack,
  Input,
  Tooltip,
  Divider,
  Heading,
  InputGroup,
  InputLeftAddon,
  Flex,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import type { ProviderInvoiceRuleView } from '@duke-hq/billing/types/rules';
import {
  deriveInvoiceAndDueDates,
  computeFinalUnitAmount,
} from '@duke-hq/svc-api/lib/billing/helpers/billableItems';
import { PRICE_RULE_TYPES } from '@duke-hq/billing/helpers/rules';
import { RULE_TYPE_SHORT_LABELS } from './constants';
import { getRuleParams } from './utils';
import { EVENT_TYPES, type EventType } from '@duke-hq/billing/types/billableItems';

const EVENT_TYPE_LABELS: Record<EventType, string> = {
  LLN_COMPLETED: 'LLN Completed',
  COURSE_ENROLMENT_CREATED: 'Course Enrolment Created',
  SEMESTER_2_ENROLMENT_CREATED: 'Semester 2 Enrolment Created',
};

function deriveInvoiceAndDueDatesForPreview(
  rules: ProviderInvoiceRuleView[],
  baseNowUTC: Date,
): { invoiceDate: Date; dueDate: Date; appliedInvoiceDayRuleId: string | null } {
  const { invoiceDate, dueDate } = deriveInvoiceAndDueDates(rules, baseNowUTC);

  const year = baseNowUTC.getUTCFullYear();
  const month = baseNowUTC.getUTCMonth();
  const todayDay = baseNowUTC.getUTCDate();
  const maxDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();

  const dayRules = rules.filter(
    (r): r is ProviderInvoiceRuleView & { type: 'INVOICE_DAY_OF_MONTH'; dayOfMonth: number } =>
      r.type === 'INVOICE_DAY_OF_MONTH' && typeof r.dayOfMonth === 'number',
  );

  let appliedInvoiceDayRuleId: string | null = null;
  if (dayRules.length > 0) {
    const clampedDayRules = dayRules
      .map((r) => ({ id: r.id, day: Math.max(1, Math.min(r.dayOfMonth, maxDay)) }))
      .sort((a, b) => a.day - b.day);

    const candidate = clampedDayRules.find((r) => r.day >= todayDay);
    if (candidate != null) {
      appliedInvoiceDayRuleId = candidate.id;
    } else {
      // Rolled to next month — the earliest day rule applies
      const rawDayRules = dayRules
        .map((r) => ({ id: r.id, day: r.dayOfMonth }))
        .sort((a, b) => a.day - b.day);
      appliedInvoiceDayRuleId = rawDayRules[0]?.id ?? null;
    }
  }

  return { invoiceDate, dueDate, appliedInvoiceDayRuleId };
}

function getDefaultBaseDate(eventType: EventType): Date {
  return eventType === 'SEMESTER_2_ENROLMENT_CREATED' ? getDefaultSemester2StartDate() : new Date();
}

function getDefaultSemester2StartDate(): Date {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), 6, 1));
}

function formatDateDisplay(d: Date): string {
  return d.toLocaleDateString('en-AU', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function toInputDateValue(d: Date): string {
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function fromInputDateValue(value: string): Date | null {
  const parts = value.split('-');
  if (parts.length !== 3) {
    return null;
  }
  const [yearStr, monthStr, dayStr] = parts;
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);
  if (isNaN(year) || isNaN(month) || isNaN(day)) {
    return null;
  }
  return new Date(Date.UTC(year, month - 1, day));
}

const DEFAULT_BASE_PRICE = '990.00';

type PriceStep = {
  rule: ProviderInvoiceRuleView;
  priceBefore: number;
  priceAfter: number;
};

function computePricePreview(
  basePrice: string,
  rules: ProviderInvoiceRuleView[],
  courseSegment: 'Full course' | 'First semester only' | 'Second semester only',
): { finalPrice: number; steps: PriceStep[] } {
  const segment = 'Full course';
  const emptyFirstN = new Set<string>();
  const steps: PriceStep[] = [];

  const priceRules = rules.filter((r) => PRICE_RULE_TYPES.has(r.type));
  for (const [i, rule] of priceRules.entries()) {
    const priceBefore = Number(
      computeFinalUnitAmount(basePrice, priceRules.slice(0, i), emptyFirstN, segment),
    );
    const priceAfter = Number(
      computeFinalUnitAmount(basePrice, priceRules.slice(0, i + 1), emptyFirstN, segment),
    );
    if (priceBefore !== priceAfter) {
      steps.push({ rule, priceBefore, priceAfter });
    }
  }

  const finalPrice = Number(computeFinalUnitAmount(basePrice, rules, emptyFirstN, courseSegment));

  return { finalPrice, steps };
}

function formatAud(n: number): string {
  return n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });
}

const COURSE_SEGMENTS = ['Full course', 'First semester only', 'Second semester only'] as const;

type PreviewRow = {
  eventType: EventType;
  baseDate: Date;
  semester2StartDate: Date | null;
  invoiceDate: Date;
  dueDate: Date;
  appliedRules: ProviderInvoiceRuleView[];
  appliedInvoiceDayRuleId: string | null;
  dateChanged: boolean;
};

type BillingRulesPreviewModalProps = {
  isOpen: boolean;
  onClose: () => void;
  rules: ProviderInvoiceRuleView[];
};

export function BillingRulesPreviewModal({
  isOpen,
  onClose,
  rules,
}: BillingRulesPreviewModalProps) {
  const [baseDateOverrides, setBaseDateOverrides] = useState<Partial<Record<EventType, Date>>>({});
  const [semester2StartDate, setSemester2StartDate] = useState<Date>(() =>
    getDefaultSemester2StartDate(),
  );
  const [basePrice, setBasePrice] = useState(DEFAULT_BASE_PRICE);

  const handleBaseDateChange = useCallback((eventType: EventType, value: string) => {
    const parsed = fromInputDateValue(value);
    if (parsed != null) {
      setBaseDateOverrides((prev) => ({ ...prev, [eventType]: parsed }));
    }
  }, []);

  const handleSemester2StartDateChange = useCallback((value: string) => {
    const parsed = fromInputDateValue(value);
    if (parsed != null) {
      setSemester2StartDate(parsed);
    }
  }, []);

  const activeRules = rules.filter((r) => r.active);
  const dateRules = activeRules.filter(
    (r) => r.type === 'INVOICE_DAY_OF_MONTH' || r.type === 'DUE_TERMS_NET_DAYS',
  );
  const priceRules = activeRules.filter((r) => PRICE_RULE_TYPES.has(r.type));

  const rows: PreviewRow[] = EVENT_TYPES.map((eventType) => {
    const baseDate = baseDateOverrides[eventType] ?? getDefaultBaseDate(eventType);

    const effectiveInvoiceBase =
      eventType === 'SEMESTER_2_ENROLMENT_CREATED' ? semester2StartDate : baseDate;

    const { invoiceDate, dueDate, appliedInvoiceDayRuleId } = deriveInvoiceAndDueDatesForPreview(
      dateRules,
      effectiveInvoiceBase,
    );

    const defaultResult = deriveInvoiceAndDueDates([], effectiveInvoiceBase);
    const dateChanged =
      invoiceDate.getTime() !== defaultResult.invoiceDate.getTime() ||
      dueDate.getTime() !== defaultResult.dueDate.getTime();

    return {
      eventType,
      baseDate,
      semester2StartDate: eventType === 'SEMESTER_2_ENROLMENT_CREATED' ? semester2StartDate : null,
      invoiceDate,
      dueDate,
      appliedRules: dateRules,
      appliedInvoiceDayRuleId,
      dateChanged,
    };
  });

  const activeDateRuleCount = rules.filter(
    (r) => r.active && (r.type === 'INVOICE_DAY_OF_MONTH' || r.type === 'DUE_TERMS_NET_DAYS'),
  ).length;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Heading size="sm">
            <Trans>Billing rules preview</Trans>
          </Heading>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Heading size="xs">
            <Trans>Invoice on day of month</Trans>
          </Heading>
          <Stack spacing={4}>
            <Text fontSize="sm" color="gray.500">
              Showing how active date rules affect invoice and due dates for each event type. Change
              the dates below to see how rules react.
              {activeDateRuleCount === 0 && ' No active date rules — defaults are shown.'}
            </Text>

            <Box
              overflowX="auto"
              sx={{
                '&::-webkit-scrollbar': { width: '8px', height: '8px' },
                '&::-webkit-scrollbar-track': { bg: 'gray.100' },
                '&::-webkit-scrollbar-thumb': { bg: 'gray.400', borderRadius: 'md' },
              }}
            >
              <Table variant="simple" size="sm" minW="850px">
                <Thead
                  position="sticky"
                  top={0}
                  bg="bg.surface"
                  zIndex={1}
                  _dark={{ bg: 'gray.800' }}
                >
                  <Tr>
                    <Th>Event type</Th>
                    <Th>
                      <Tooltip label="The date the billable event was created" placement="top">
                        <Text cursor="help">Billable event created</Text>
                      </Tooltip>
                    </Th>
                    <Th>
                      <Tooltip
                        label="For Semester 2, the invoice date is derived from when the semester starts rather than the event creation date"
                        placement="top"
                      >
                        <Text cursor="help">Semester 2 start</Text>
                      </Tooltip>
                    </Th>
                    <Th>Invoice date</Th>
                    <Th>Due date</Th>
                    <Th>Rules applied</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {rows.map((row) => (
                    <Tr
                      key={row.eventType}
                      bg={row.dateChanged ? 'blue.50' : undefined}
                      _dark={{ bg: row.dateChanged ? 'blue.900' : undefined }}
                    >
                      <Td fontWeight="medium">{EVENT_TYPE_LABELS[row.eventType]}</Td>
                      <Td>
                        <Input
                          type="date"
                          size="xs"
                          w="150px"
                          value={toInputDateValue(row.baseDate)}
                          onChange={(e) => handleBaseDateChange(row.eventType, e.target.value)}
                        />
                      </Td>
                      <Td>
                        {row.semester2StartDate != null ? (
                          <Input
                            type="date"
                            size="xs"
                            w="150px"
                            value={toInputDateValue(row.semester2StartDate)}
                            onChange={(e) => handleSemester2StartDateChange(e.target.value)}
                          />
                        ) : (
                          <Text color="gray.400" fontSize="sm">
                            —
                          </Text>
                        )}
                      </Td>
                      <Td fontWeight={row.dateChanged ? 'semibold' : 'normal'}>
                        {formatDateDisplay(row.invoiceDate)}
                      </Td>
                      <Td fontWeight={row.dateChanged ? 'semibold' : 'normal'}>
                        {formatDateDisplay(row.dueDate)}
                      </Td>
                      <Td>
                        {row.appliedRules.length === 0 ? (
                          <Text color="gray.400" fontSize="sm">
                            Default (+14 days)
                          </Text>
                        ) : (
                          row.appliedRules.map((r) => {
                            const params = getRuleParams(r);
                            const valueStr = params.map((p) => `${p.label}: ${p.value}`).join(', ');
                            const isAppliedDayRule =
                              r.type === 'INVOICE_DAY_OF_MONTH' &&
                              r.id === row.appliedInvoiceDayRuleId;
                            const isSkippedDayRule =
                              r.type === 'INVOICE_DAY_OF_MONTH' &&
                              row.appliedInvoiceDayRuleId != null &&
                              r.id !== row.appliedInvoiceDayRuleId;
                            const isDayRuleFallback =
                              r.type === 'INVOICE_DAY_OF_MONTH' &&
                              row.appliedInvoiceDayRuleId == null;
                            return (
                              <Tooltip
                                key={r.id}
                                label={
                                  isAppliedDayRule
                                    ? 'This rule determines the invoice date (nearest future day of month)'
                                    : isSkippedDayRule
                                      ? 'Not applied — another invoice day rule is closer to the event date'
                                      : isDayRuleFallback
                                        ? 'Not applied — all configured days are in the past for this month'
                                        : undefined
                                }
                                placement="top"
                                isDisabled={
                                  !isAppliedDayRule && !isSkippedDayRule && !isDayRuleFallback
                                }
                              >
                                <Badge
                                  borderRadius="full"
                                  mr={1}
                                  mb={1}
                                  colorScheme={isAppliedDayRule ? 'green' : undefined}
                                  opacity={isSkippedDayRule || isDayRuleFallback ? 0.5 : 1}
                                >
                                  {RULE_TYPE_SHORT_LABELS[r.type]}
                                  {valueStr !== '' ? ` (${valueStr})` : ''}
                                </Badge>
                              </Tooltip>
                            );
                          })
                        )}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
            <Divider />

            <Heading size="xs">
              <Trans>Price</Trans>
            </Heading>

            <Flex align="center" gap={4}>
              <Text fontSize="sm" color="gray.500" whiteSpace="nowrap">
                Base course price
              </Text>
              <InputGroup size="sm" w="180px">
                <InputLeftAddon h="auto">AUD</InputLeftAddon>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={basePrice}
                  onChange={(e) => setBasePrice(e.target.value)}
                />
              </InputGroup>
              {priceRules.length === 0 && (
                <Text fontSize="sm" color="gray.500">
                  No active price rules.
                </Text>
              )}
            </Flex>

            {priceRules.length > 0 && (
              <Box
                overflowX="auto"
                sx={{
                  '&::-webkit-scrollbar': { width: '8px', height: '8px' },
                  '&::-webkit-scrollbar-track': { bg: 'gray.100' },
                  '&::-webkit-scrollbar-thumb': { bg: 'gray.400', borderRadius: 'md' },
                }}
              >
                <Table variant="simple" size="sm" minW="650px">
                  <Thead
                    position="sticky"
                    top={0}
                    bg="bg.surface"
                    zIndex={1}
                    _dark={{ bg: 'gray.800' }}
                  >
                    <Tr>
                      <Th>Course segment</Th>
                      <Th>Base price</Th>
                      <Th>Rules applied</Th>
                      <Th isNumeric>Final price</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {COURSE_SEGMENTS.map((segment) => {
                      const { finalPrice, steps } = computePricePreview(
                        basePrice,
                        priceRules,
                        segment,
                      );
                      const priceChanged = finalPrice !== Number(basePrice);
                      const isHalved = segment !== 'Full course';
                      return (
                        <Tr
                          key={segment}
                          bg={priceChanged ? 'blue.50' : undefined}
                          _dark={{ bg: priceChanged ? 'blue.900' : undefined }}
                        >
                          <Td fontWeight="medium">{segment}</Td>
                          <Td>{formatAud(Number(basePrice))}</Td>
                          <Td>
                            {steps.length === 0 && !isHalved ? (
                              <Text color="gray.400" fontSize="sm">
                                No price rules applied
                              </Text>
                            ) : (
                              <Stack spacing={0}>
                                {steps.map((step) => {
                                  const params = getRuleParams(step.rule);
                                  const valueStr = params
                                    .map((p) => `${p.label}: ${p.value}`)
                                    .join(', ');
                                  return (
                                    <Tooltip
                                      key={step.rule.id}
                                      label={`${formatAud(step.priceBefore)} → ${formatAud(step.priceAfter)}`}
                                      placement="top"
                                    >
                                      <Badge
                                        borderRadius="full"
                                        mr={1}
                                        mb={1}
                                        colorScheme="green"
                                        cursor="help"
                                      >
                                        {RULE_TYPE_SHORT_LABELS[step.rule.type]}
                                        {valueStr !== '' ? ` (${valueStr})` : ''}
                                      </Badge>
                                    </Tooltip>
                                  );
                                })}
                                {isHalved ? (
                                  <Badge borderRadius="full" mr={1} mb={1} colorScheme="purple">
                                    Semester ÷ 2
                                  </Badge>
                                ) : null}
                              </Stack>
                            )}
                          </Td>
                          <Td isNumeric fontWeight={priceChanged ? 'semibold' : 'normal'}>
                            {formatAud(finalPrice)}
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>
            )}
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
